import React, { useState, useEffect, useRef } from 'react';
import { X, ExternalLink, Calendar, MapPin, Download, CheckCircle, AlertCircle, AlertTriangle, FileText, Link as LinkIcon, ZoomIn, ZoomOut, Move, MessageSquare, Send } from 'lucide-react';
import { doc, getDoc, updateDoc, increment, arrayUnion, collection, addDoc, query, where, orderBy, onSnapshot, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../utils/firebase';
import { portalDb } from '../utils/portalFirebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { toast } from 'react-hot-toast';

const AdminReportModal = ({ reportId, onClose }) => {
  const [user] = useAuthState(auth);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  
  // Add touch-related state
  const [touchDistance, setTouchDistance] = useState(null);
  
  // Add chat-related state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Fetch the report
  useEffect(() => {
    let isMounted = true;
    
    const fetchReport = async () => {
      setIsLoading(true);
      
      try {
        const reportRef = doc(db, 'inspections', reportId);
        const reportDoc = await getDoc(reportRef);
        
        if (!isMounted) return;
        
        if (reportDoc.exists()) {
          setReport(reportDoc.data());
        } else {
          toast.error('Report not found');
          if (isMounted) onClose();
        }
      } catch (error) {
        console.error('Error fetching report:', error);
        toast.error('Error loading report');
        if (isMounted) onClose();
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchReport();
    
    // Ensure modal can be closed with ESC key
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      isMounted = false;
      window.removeEventListener('keydown', handleEsc);
    };
  }, [reportId, onClose]);

  // Fetch messages for this report
  useEffect(() => {
    if (!reportId || !user) return;
    
    setIsLoadingMessages(true);
    
    // Create a query against the messages collection
    const messagesRef = collection(portalDb, 'reportMessages');
    const q = query(
      messagesRef,
      where('reportId', '==', reportId),
      orderBy('timestamp', 'asc')
    );
    
    // Listen for real-time updates
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const messagesList = [];
      querySnapshot.forEach((doc) => {
        messagesList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setMessages(messagesList);
      setIsLoadingMessages(false);
      
      // Mark messages as read if they're from client
      if (activeTab === 'messages') {
        markMessagesAsRead(messagesList);
      }
      
      // Scroll to bottom when new messages arrive
      setTimeout(() => {
        if (messagesEndRef.current && activeTab === 'messages') {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    });
    
    // Clean up listener
    return () => unsubscribe();
  }, [reportId, user, activeTab]);
  
  // Mark client messages as read when inspector views them
  const markMessagesAsRead = async (messagesList) => {
    // Process each message individually instead of using batch
    const unreadMessages = messagesList.filter(msg => 
      msg.senderId !== user.uid && !msg.read
    );
    
    // If there are unread messages, mark them as read
    if (unreadMessages.length > 0) {
      try {
        // Process each message update individually
        const updatePromises = unreadMessages.map(msg => {
          const messageRef = doc(portalDb, 'reportMessages', msg.id);
          return updateDoc(messageRef, { read: true });
        });
        
        // Wait for all updates to complete
        await Promise.all(updatePromises);
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    }
  };
  
  // Send a new message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !user || !reportId) return;
    
    try {
      await addDoc(collection(portalDb, 'reportMessages'), {
        reportId,
        senderId: user.uid,
        senderName: user.displayName || user.email,
        senderType: 'inspector',
        message: newMessage.trim(),
        timestamp: new Date(),
        read: false
      });
      
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  // Format address by removing USA
  const formatAddress = (address) => {
    if (!address) return 'Address not available';
    return address
      .replace(/,\s*USA$/i, '')
      .replace(/\s*USA$/i, '')
      .replace(/,\s*United States$/i, '')
      .replace(/,\s*New Jersey$/i, '')
      .replace(/,\s*NJ$/i, '');
  };

  // Get scenario icon and information
  const getScenarioInfo = (scenario) => {
    switch(scenario) {
      case 'noTank':
        return {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
          label: 'No underground storage tank detected',
          color: 'text-green-500',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'anomaly':
        return {
          icon: <AlertCircle className="w-5 h-5 text-amber-500" />,
          label: 'Metallic anomaly detected',
          color: 'text-amber-500',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200'
        };
      case 'ustFound':
      case 'tankFound':
        return {
          icon: <AlertTriangle className="w-5 h-5 text-red-500" />,
          label: 'Underground storage tank identified',
          color: 'text-red-500',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
      default:
        return {
          icon: <AlertCircle className="w-5 h-5 text-gray-500" />,
          label: 'Status unknown',
          color: 'text-gray-500',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Render report content
  const renderReportContent = () => {
    if (!report) return null;

    // Add Resources Section
    const resourcesSection = report.resources && report.resources.length > 0 ? (
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Additional Resources
        </h4>
        <ul className="space-y-2">
          {report.resources.map((resource, index) => (
            <li 
              key={index} 
              className="flex items-center p-2 bg-white rounded border border-gray-200"
            >
              {resource.type === 'file' ? (
                <FileText className="h-4 w-4 text-blue-500 mr-3 flex-shrink-0" />
              ) : (
                <LinkIcon className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
              )}
              <div className="flex-grow overflow-hidden">
                <p className="text-sm font-medium text-gray-800 truncate">{resource.title}</p>
              </div>
              <a 
                href={resource.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-3 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors duration-150 flex items-center"
              >
                View <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

    // If the report has fullDocumentText, we'll use that directly
    if (report.fullDocumentText) {
      // Split by paragraphs and render with spacing
      return (
        <>
          {/* Insert Resources Section at the top */}
          {resourcesSection}
          
          {/* Original report content */}
          {report.fullDocumentText.split('\n\n').map((paragraph, idx) => (
            <p key={idx} className="mb-4">{paragraph}</p>
          ))}
        </>
      );
    }

    // Otherwise, we'll build it from the individual fields
    let content = [];
    
    // Insert Resources Section at the top
    if (resourcesSection) {
      content.push(resourcesSection);
    }

    // Introduction with the appropriate conclusion based on scenario
    let introText = "Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). ";
    
    if (report.scenario === 'noTank') {
      introText += "We did not detect any anomalies with our metal detection equipment that match the size of a UST.";
      
      // Add limitations text from the report data
      if (report.limitations?.length > 0) {
        introText += " Our inspection was limited by " + 
          report.limitations.map(limitation => {
            const type = limitation.type === 'custom' ? limitation.customType : limitation.type;
            const location = limitation.location === 'custom' ? limitation.customLocation : limitation.location;
            return `${type} ${location}`;
          }).join(", ") + ".";
      }
      
      // Add custom text if available
      if (report.customText) {
        introText += ` ${report.customText}`;
      }
      
      introText += " This does not guarantee the absence of USTs on the property; but it is unlikely.";
    } 
    else if (report.scenario === 'anomaly') {
      introText += `A metallic anomaly, similar in size to an underground storage tank (UST), was detected${
        report.anomalyLocation ? " " + (report.anomalyLocation === 'custom' ? report.customLocation : report.anomalyLocation) : ""
      }.`;
      
      // Add limitations text from the report data
      if (report.limitations?.length > 0) {
        introText += " Our inspection was limited by " + 
          report.limitations.map(limitation => {
            const type = limitation.type === 'custom' ? limitation.customType : limitation.type;
            const location = limitation.location === 'custom' ? limitation.customLocation : limitation.location;
            return `${type} ${location}`;
          }).join(", ") + ".";
      }
      
      // Add custom text if available
      if (report.customText) {
        introText += ` ${report.customText}`;
      }
      
      introText += " We recommend requesting OPRA records for any information on previously decommissioned oil tanks. If no records are found, proceed with an intrusive investigation.";
    } 
    else if (report.scenario === 'ustFound' || report.scenario === 'tankFound') {
      introText += `An underground storage tank (UST) was identified${
        report.anomalyLocation ? " " + (report.anomalyLocation === 'custom' ? report.customLocation : report.anomalyLocation) : ""
      }.`;
      
      // Add limitations text from the report data
      if (report.limitations?.length > 0) {
        introText += " Our inspection was limited by " + 
          report.limitations.map(limitation => {
            const type = limitation.type === 'custom' ? limitation.customType : limitation.type;
            const location = limitation.location === 'custom' ? limitation.customLocation : limitation.location;
            return `${type} ${location}`;
          }).join(", ") + ".";
      }
      
      // Add custom text if available
      if (report.customText) {
        introText += ` ${report.customText}`;
      }
      
      introText += " This UST may pose a risk of contamination if not addressed. We strongly recommend the removal or proper decommissioning of this UST, along with soil testing for potential contamination.";
    }

    content.push(<p key="intro" className="mb-4">{introText}</p>);

    // Standard equipment and methodology text
    content.push(
      <p key="equipment" className="mb-4">
        Utilizing electronic metal detection equipment (Fisher TW-6), and a magnetic locating device (Schonstedt 92XTD), 
        the grounds were swept for any buried metallic objects representing a UST. The detection equipment used can detect
        metallic objects up to a depth of six feet. The property was swept to a distance of at least 30ft around the 
        perimeter of the house.
      </p>
    );

    content.push(
      <p key="conclusion2" className="mb-4">
        No other metal anomalies consistent with the size of an underground storage tank were identified during the 
        oil tank sweep.
      </p>
    );

    content.push(
      <p key="contact" className="mb-4">
        If you have any questions about this report please feel free to contact us.
      </p>
    );

    content.push(
      <p key="terms" className="mb-4 text-sm text-gray-600">
        Terms and Conditions: This report excludes areas where access to the magnetic locator was restricted due to 
        obstructions such as dense vegetation, denied access, or structural interference. The methods used at this 
        site have been used successfully at many sites to locate underground storage tanks and drums. There can be 
        no guarantee, however, that every target will be detected at a particular site. Subsurface conditions (e.g. 
        rebar/wire mesh under concrete, foundations, structures and others) may prevent some or all geophysical 
        locating methods from working properly.
      </p>
    );

    return content;
  };

  // Image handling functions
  const handleImageClick = () => {
    setShowImageZoom(true);
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
  };

  const handleCloseZoom = () => {
    setShowImageZoom(false);
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ 
        x: e.clientX - panPosition.x, 
        y: e.clientY - panPosition.y 
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add touch handlers for pinch zoom
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Calculate initial distance between two touch points for pinch-to-zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      setTouchDistance(distance);
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // Single touch for panning
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - panPosition.x,
        y: e.touches[0].clientY - panPosition.y
      });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent default to avoid page scrolling

    if (e.touches.length === 2 && touchDistance !== null) {
      // Handle pinch zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const newDistance = Math.sqrt(dx * dx + dy * dy);
      
      if (newDistance > touchDistance) {
        // Pinch out - zoom in
        setZoomLevel(prev => Math.min(prev + 0.05, 5));
      } else {
        // Pinch in - zoom out
        setZoomLevel(prev => Math.max(prev - 0.05, 1));
      }
      
      setTouchDistance(newDistance);
    } else if (e.touches.length === 1 && isDragging && zoomLevel > 1) {
      // Handle panning with single touch
      setPanPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setTouchDistance(null);
  };

  // Handle double-click to reset zoom
  const handleDoubleClick = (e) => {
    if (showImageZoom) {
      if (zoomLevel === 1) {
        // Double-click to zoom in
        setZoomLevel(2);
        
        // Zoom in centered on the click position
        const container = imageRef.current.parentElement;
        const containerRect = container.getBoundingClientRect();
        
        const clickX = e.clientX - containerRect.left;
        const clickY = e.clientY - containerRect.top;
        
        const centerX = containerRect.width / 2;
        const centerY = containerRect.height / 2;
        
        // Calculate the pan position to center on the click point
        setPanPosition({
          x: centerX - (clickX - centerX),
          y: centerY - (clickY - centerY)
        });
      } else {
        // Double-click again to reset zoom
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
      }
    }
  };

  // Handle downloading the PDF
  const handleDownloadPdf = () => {
    if (report?.pdfUrl) {
      window.open(report.pdfUrl, '_blank');
    }
  };

  // Add function to delete a message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      // Reference to the message document
      const messageRef = doc(portalDb, 'reportMessages', messageId);
      
      // Delete the message
      await deleteDoc(messageRef);
      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-start gap-3">
            {/* Address and details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {formatAddress(report?.propertyAddress || '')}
              </h3>
              <p className="text-sm text-gray-500">{report?.clientName || 'Unknown'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs navigation */}
        <div className="px-4 border-b">
          <div className="flex overflow-x-auto">
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === 'details'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => setActiveTab('details')}
            >
              Report Details
            </button>
            
            {report?.imageUrls?.length > 0 && (
              <button
                className={`px-4 py-2 font-medium ${
                  activeTab === 'images'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('images')}
              >
                Images ({report.imageUrls.length})
              </button>
            )}
            
            {/* Add Messages tab */}
            <button
              className={`px-4 py-2 font-medium flex items-center gap-1 ${
                activeTab === 'messages'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => setActiveTab('messages')}
            >
              <MessageSquare className="w-4 h-4" />
              Messages
              {messages.filter(m => m.senderId !== user?.uid && !m.read).length > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {messages.filter(m => m.senderId !== user?.uid && !m.read).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
          ) : !report ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-gray-500">Report not found</p>
            </div>
          ) : (
            <>
              {activeTab === 'details' && (
                <div>
                  {/* Inspection information */}
                  <div className="mb-6">
                    <div className="flex items-center mb-2">
                      <Calendar className="h-5 w-5 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-500">Inspection Date:</span>
                      <span className="text-sm font-medium ml-2">
                        {formatDate(report.dateOfInspection || report.inspectionDate || report.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-500">Property:</span>
                      <span className="text-sm font-medium ml-2">
                        {formatAddress(report.propertyAddress)}
                      </span>
                    </div>
                  </div>

                  {/* Inspection findings */}
                  {report.scenario && (
                    <div className={`mb-6 p-3 rounded-md ${getScenarioInfo(report.scenario).bgColor} ${getScenarioInfo(report.scenario).borderColor} border`}>
                      <div className="flex items-center">
                        {getScenarioInfo(report.scenario).icon}
                        <span className={`ml-2 text-sm font-medium ${getScenarioInfo(report.scenario).color}`}>
                          {getScenarioInfo(report.scenario).label}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Report text */}
                  <div className="prose prose-sm max-w-none">
                    {renderReportContent()}
                  </div>

                  {/* PDF Download button */}
                  {report.pdfUrl && (
                    <div className="mt-6">
                      <button 
                        onClick={handleDownloadPdf}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        View Full Report
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'images' && report?.imageUrls && (
                <div>
                  {report.imageUrls.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {report.imageUrls.map((img, idx) => (
                          <div 
                            key={idx}
                            className={`relative aspect-square overflow-hidden rounded border-2 cursor-pointer ${
                              idx === activeImageIndex ? 'border-blue-500' : 'border-transparent'
                            }`}
                            onClick={() => setActiveImageIndex(idx)}
                          >
                            <img 
                              src={img.url} 
                              alt={`Site photo ${idx + 1}`}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                      
                      {/* Selected image view */}
                      <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative">
                        {report.imageUrls[activeImageIndex] && (
                          <img 
                            src={report.imageUrls[activeImageIndex].url} 
                            alt={`Selected site photo`}
                            className="w-full h-full object-contain cursor-zoom-in"
                            onClick={handleImageClick}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No images available</p>
                  )}
                </div>
              )}
              
              {activeTab === 'messages' && (
                <div className="flex flex-col h-full">
                  <h2 className="text-xl font-semibold mb-4">Client Communication</h2>
                  
                  {isLoadingMessages ? (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 overflow-y-auto mb-4 space-y-3">
                        {messages.length === 0 ? (
                          <div className="text-center text-gray-500 py-8">
                            No messages yet. Start a conversation with the client about this report.
                          </div>
                        ) : (
                          messages.map((msg) => (
                            <div 
                              key={msg.id} 
                              className={`p-3 rounded-lg max-w-[80%] ${
                                msg.senderId === user?.uid 
                                  ? 'ml-auto bg-blue-500 text-white' 
                                  : 'bg-gray-100'
                              }`}
                            >
                              <div className="text-xs opacity-70 mb-1 flex justify-between">
                                <span>{msg.senderName} ({msg.senderType}) • {new Date(msg.timestamp.toDate()).toLocaleString()}</span>
                                {msg.senderId === user?.uid && (
                                  <button 
                                    onClick={() => handleDeleteMessage(msg.id)}
                                    className="ml-2 text-white hover:text-red-200"
                                    title="Delete message"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                              <div>{msg.message}</div>
                            </div>
                          ))
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                      
                      <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message to client..."
                          className="flex-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="submit"
                          disabled={!newMessage.trim()}
                          className="p-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </form>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Image zoom overlay */}
      {showImageZoom && report?.imageUrls && report.imageUrls[activeImageIndex] && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onDoubleClick={handleDoubleClick}
        >
          <div className="absolute top-4 right-4 flex items-center space-x-2 z-10">
            <div className="text-white bg-gray-800 bg-opacity-50 rounded-full px-3 py-1 text-sm">
              {Math.round(zoomLevel * 100)}%
            </div>
            <button 
              onClick={handleZoomIn}
              className="text-white hover:text-blue-300 bg-gray-800 bg-opacity-50 rounded-full p-2"
            >
              <ZoomIn className="h-5 w-5" />
            </button>
            <button 
              onClick={handleZoomOut}
              className="text-white hover:text-blue-300 bg-gray-800 bg-opacity-50 rounded-full p-2"
            >
              <ZoomOut className="h-5 w-5" />
            </button>
            <button 
              onClick={handleCloseZoom}
              className="text-white hover:text-red-300 bg-gray-800 bg-opacity-50 rounded-full p-2"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {zoomLevel > 1 && (
            <div className="absolute bottom-4 left-4 text-white bg-gray-800 bg-opacity-50 rounded-full p-2 flex items-center">
              <Move className="h-5 w-5 mr-2" />
              <span className="text-sm">Click and drag to pan</span>
            </div>
          )}
          
          <img 
            ref={imageRef}
            src={report.imageUrls[activeImageIndex].url} 
            alt="Zoomed view"
            className={`${isDragging ? 'cursor-grabbing' : zoomLevel > 1 ? 'cursor-grab' : 'cursor-zoom-in'} select-none`}
            style={{
              transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
              maxWidth: '90vw',
              maxHeight: '90vh',
              transition: isDragging ? 'none' : 'transform 0.2s'
            }}
          />
          
          {/* Navigation buttons for mobile */}
          <div className="absolute bottom-4 right-4 flex items-center space-x-2">
            {activeImageIndex > 0 && (
              <button 
                onClick={() => {
                  setActiveImageIndex(prev => prev - 1);
                  setZoomLevel(1);
                  setPanPosition({ x: 0, y: 0 });
                }}
                className="text-white hover:text-blue-300 bg-gray-800 bg-opacity-50 rounded-full p-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
            {activeImageIndex < report.imageUrls.length - 1 && (
              <button 
                onClick={() => {
                  setActiveImageIndex(prev => prev + 1);
                  setZoomLevel(1);
                  setPanPosition({ x: 0, y: 0 });
                }}
                className="text-white hover:text-blue-300 bg-gray-800 bg-opacity-50 rounded-full p-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReportModal; 